import { Formik, Form } from 'formik'
import { memo, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'ramda'
import {
  Box,
  CheckboxStandalone,
  FieldMessage,
  Stack,
  Text,
  Inline
} from '@machobear/design-system'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Button } from '@/components/button'
import ContentWrapper from '@/components/content-wrapper'
import { Error } from '@/components/form'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import AreaTitle from '@/components/area-title'
import { toast } from '@/components/toast-notifications'
import { EmploymentInfoForm } from '@/features/Profile/EmploymentInfo'
import { DefaultButtonBar } from '@/components/button-bar'
import { select } from '@/store'
import { breakpoints } from '@/styles'
import { CurrencyField } from '@/components/ds-form'
import UploadIncomeForm from './upload-income-form'

const ErrorWrapper = styled.div`
  margin: -40px 1px 0;
  padding: 0px 30px 40px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: -40px 11px 0;
  }
`

const OLD_EXPLICIT_INCOME_VALUES = ['No Income', 'Rent Prepay']
const OLD_INCOME_VALUES = [
  ...OLD_EXPLICIT_INCOME_VALUES,
  '1-29k',
  '30k-59k',
  '60k-79k',
  '80k-99k',
  '100k-149k',
  '150k+'
]

const EditLivScore = ({ history, location }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const data = useSelector(select.profile.editTenantProfile)
  const employmentFile = data?.employment_verification
  const studentFile = data?.school_verification

  const getSchoolVerificationBody = (values, incomeBody) => {
    const schoolBody = {
      data: values.school_verification,
      tag: 'school_verification',
      metadata: {
        document_type: values.school_document_type?.type
      }
    }
    if (values.income_verification.id) {
      return [schoolBody]
    }

    if (values.school_verification.id) {
      return [incomeBody]
    }

    return [incomeBody, schoolBody]
  }

  const getEmploymentVerificationBody = (values, incomeBody) => {
    const employmentBody = {
      data: values.employment_verification,
      tag: 'employment_verification',
      metadata: {
        document_type: values.employment_document_type?.type
      }
    }
    if (values.income_verification.id) {
      return [employmentBody]
    }

    if (values.employment_verification.id) {
      return [incomeBody]
    }
    return [incomeBody, employmentBody]
  }

  const handleSend = values => {
    let portTag = 'affordability_verification'
    const body = (() => {
      if (values.job_title === 'student') {
        portTag = 'school_verification'
        const incomeBody = {
          data: values.income_verification,
          tag: 'income_verification',
          metadata: {
            document_type: values.income_document_type?.type,
            user: {
              salary: values.salary || null
            }
          }
        }
        return getSchoolVerificationBody(values, incomeBody)
      } else if (values.job_title === 'unemployed') {
        return [
          {
            data: values.income_verification,
            tag: 'income_verification',
            metadata: {
              document_type: values.income_document_type?.type,
              user: {
                job_title: values.job_title,
                salary: values.salary || null
              }
            }
          }
        ]
      } else if (values.job_title === 'self_employed') {
        const incomeBody = {
          data: values.income_verification,
          tag: 'income_verification',
          metadata: {
            document_type: values.income_document_type?.type,
            user: {
              job_title: 'self_employed',
              company_info: {
                name: values.company_name || null
              },
              salary: values.salary || null
            }
          }
        }
        return getEmploymentVerificationBody(values, incomeBody)
      } else {
        const incomeBody = {
          data: values.income_verification,
          tag: 'income_verification',
          metadata: {
            document_type: values.income_document_type?.type,
            user: {
              job_title: values.job_title,
              company_info: {
                name: values.company_name || null
              },
              salary: values.salary || null
            }
          }
        }
        return getEmploymentVerificationBody(values, incomeBody)
      }
    })()
    return dispatch.livScore.sendFile({ body, portTag })
  }

  const onFileSelected = async (setFieldValue, fileUrl, fileType) => {
    setFieldValue(fileType, fileUrl)
  }

  const handleBack = useCallback(() => {
    const state = location?.state || {}
    const { onSuccessRedirectTo, onBackRedirectTo, ...restState } = state
    history.replace({
      pathname: onBackRedirectTo || '/profile/trust-score',
      state: restState
    })
  }, [history, location])

  const salaryData = data.salary ?? null

  const initialValues = {
    ...data,
    prepay: data.salary === 'Rent Prepay' || !!Number(data.prepay),
    salary: OLD_EXPLICIT_INCOME_VALUES.includes(data.salary) ? null : salaryData,
    income_document_type: {},
    employment_document_type: {},
    school_document_type: {}
  }

  const userNoLongerAStudent = formValues => {
    return formValues.job_title !== 'student' && initialValues.job_title === 'student'
  }

  const userBecameStudentAndWasNotUnemployed = formValues =>
    formValues.job_title === 'student' &&
    initialValues.job_title &&
    initialValues.job_title !== 'student' &&
    initialValues.job_title !== 'unemployed'

  const deleteSchoolVerification = async setFieldValue => {
    setFieldValue('school_verification', '')
    await dispatch.livScore.deleteFile({
      fileId: studentFile.id,
      tag: studentFile.tag
    })
  }

  const deleteEmploymentVerification = async setFieldValue => {
    setFieldValue('employment_verification', '')
    await dispatch.livScore.deleteFile({
      fileId: employmentFile.id,
      tag: employmentFile.tag
    })
  }

  const getSalaryErrorMessage = salary => {
    if (salary.value === '' || isNil(salary.value))
      return t(
        'r.profile.edit.livscore.income_update.error',
        'Required: Please enter an annual income or select the prepay checkbox.'
      )
  }

  const formOptions = () => ({
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      job_title: Yup.string().required(),
      school_name: Yup.string().when('job_title', {
        is: 'student',
        then: Yup.string().required(),
        otherwise: Yup.string()
      }),
      school_subject: Yup.string().when('job_title', {
        is: 'student',
        then: Yup.string().required(),
        otherwise: Yup.string()
      }),
      school_start_date: Yup.number().max('2040').min('1900').when('job_title', {
        is: 'student',
        then: Yup.number().required(),
        otherwise: Yup.number()
      }),
      income_document_type: Yup.object(),
      employment_document_type: Yup.object(),
      school_document_type: Yup.object(),
      company_name: Yup.string().when(['job_title'], (jobTitle, schema) => {
        if (jobTitle !== 'student' && jobTitle !== 'unemployed') {
          return schema.required()
        }
        return schema
      }),
      income_verification: Yup.string().required(),
      salary: Yup.number()
        .integer()
        .min(0)
        .nullable()
        .test(
          'old_value',
          t('r.profile.edit.livscore.cannot_be_range.error', 'Cannot be a range'),
          val => !OLD_INCOME_VALUES.includes(val)
        )
        .test(
          'prepay',
          (x, y) => getSalaryErrorMessage(x, y),
          (val, ctx) => {
            const { prepay } = ctx.parent
            return prepay || val
          }
        )
        .test(
          'len',
          t('r.profile.edit.livscore.income_length.error', 'Must be less than 10 digits'),
          (val, ctx) => ctx?.parent?.prepay || val?.toString().length <= 10
        ),
      employment_verification: Yup.string().when(['job_title'], (jobTitle, schema) => {
        if (jobTitle !== 'student' && jobTitle !== 'unemployed') {
          return schema.required()
        }
        return schema
      }),
      school_verification: Yup.string().when(['job_title'], (jobTitle, schema) => {
        if (jobTitle === 'student') {
          return schema.required()
        }
        return schema
      })
    }),
    onSubmit: async (formValues, { setSubmitting, setFieldValue }) => {
      const employeeResponse = await dispatch.profile.save(formValues)
      if (userNoLongerAStudent(formValues)) {
        deleteSchoolVerification(setFieldValue)
      }
      if (userBecameStudentAndWasNotUnemployed(formValues)) {
        deleteEmploymentVerification(setFieldValue)
      }
      const verificationResponse =
        formValues?.income_document_type.tag ||
        formValues?.employment_document_type.tag ||
        (formValues.school_verification && !formValues.school_verification.id)
          ? await handleSend(formValues)
          : { response: { ok: true } }
      setSubmitting(false)
      const state = location?.state || {}
      const { onSuccessRedirectTo, onBackRedirectTo, ...restState } = state
      if (employeeResponse.ok && verificationResponse?.response?.ok) {
        history.push({
          pathname: onSuccessRedirectTo || '/profile/trust-score',
          state: restState
        })
        window.scrollTo(0, 0)
      }
    }
  })

  const showIncomeVerificationToast = () =>
    toast(
      t(
        'r.profile.edit.livscore.income_reupload_required.notification',
        'Please upload a new income verification document since your employment information has changed.'
      ),
      {
        type: 'warning',
        autoClose: 10000
      }
    )

  if (!data.id) return null

  return (
    <Page>
      <Helmet>
        <title>{t('r.profile.edit.liv_score.title', 'Trust Score')}</title>
      </Helmet>
      <PageTitle>{t('r.profile.edit.liv_score.title', 'Trust Score')}</PageTitle>
      <Formik {...formOptions()} initialValues={initialValues}>
        {({ values, isSubmitting, setFieldValue, isValid, errors }) => {
          const handleSalaryChange = () => {
            if (values?.income_verification?.id) {
              setFieldValue('income_verification', '')
              showIncomeVerificationToast()
            }
          }
          return (
            <Form className="flex-full">
              <ContentWrapper className="mb-50">
                <EmploymentInfoForm
                  setFieldValue={setFieldValue}
                  onFileSelected={onFileSelected}
                  values={values}
                />
                <ErrorWrapper>
                  <Error name="employment_verification" />
                  <Error name="school_verification" />
                </ErrorWrapper>
                <AreaTitle>
                  {t('r.profile.edit.income_verification.subtitle', 'Income Verification')}
                </AreaTitle>
                <Box paddingY="xlarge" paddingX="xlarge" background="white" borderRadius="large">
                  <Stack space="xlarge">
                    <Stack space="large">
                      <Text>
                        {t(
                          'r.profile.edit.annual_income.description',
                          `Please provide proof of your ability to make rental payments by submitting
                        your annual income or providing documentation that demonstrates sufficient
                        funds to prepay 12 months of rent.`
                        )}
                      </Text>
                      <Stack space="small">
                        <Box
                          display="flex"
                          alignItems="center"
                          flexDirection={{ mobile: 'column', tablet: 'row' }}
                        >
                          <Box
                            width="full"
                            marginRight={{ mobile: 'none', tablet: 'large' }}
                            marginBottom={{ mobile: 'small', tablet: 'none' }}
                          >
                            <CurrencyField
                              id="salary"
                              name="salary"
                              label={t('b.profile.general.annual_income.text', 'Annual Income')}
                              onChange={handleSalaryChange}
                              disabled={values?.prepay}
                            />
                          </Box>
                          <Box width="full">
                            <Inline align="center" space="xsmall" alignY="center">
                              <CheckboxStandalone
                                aria-labelledby="prepay"
                                onChange={e => {
                                  setFieldValue('salary', undefined)
                                  setFieldValue('prepay', e.target.checked)
                                  handleSalaryChange()
                                }}
                                disabled={values?.salary}
                                checked={values?.prepay}
                                tone={
                                  !values.prepay && (values?.salary === '' || !values.salary)
                                    ? 'critical'
                                    : 'neutral'
                                }
                              />
                              <Text id="prepay">
                                {t(
                                  'r.profile.edit.can_prepay_rent.text',
                                  'I can prepay 12 months rent'
                                )}
                              </Text>
                            </Inline>
                          </Box>
                        </Box>
                        {errors?.salary && (
                          <FieldMessage message={errors?.salary} tone="critical" />
                        )}
                      </Stack>
                    </Stack>
                    <Stack space="large">
                      <Trans i18nKey="r.profile.edit.liv_score_income_verification_description.text">
                        <Text>
                          Provide an additional document to verify your income. Acceptable documents
                          include{' '}
                          <Box component="span" style={{ fontWeight: '500' }}>
                            T4
                          </Box>
                          ,{' '}
                          <Box component="span" style={{ fontWeight: '500' }}>
                            Tax Slips
                          </Box>
                          ,{' '}
                          <Box component="span" style={{ fontWeight: '500' }}>
                            Pay Slip
                          </Box>
                          ,{' '}
                          <Box component="span" style={{ fontWeight: '500' }}>
                            Student Loans
                          </Box>
                          ,{' '}
                          <Box component="span" style={{ fontWeight: '500' }}>
                            Bank Statement
                          </Box>
                          , and{' '}
                          <Box component="span" style={{ fontWeight: '500' }}>
                            Job Offer Letter
                          </Box>
                          . Your documents are only used for verification and never shared with
                          other people.
                        </Text>
                      </Trans>
                      <Stack space="small">
                        <UploadIncomeForm />
                        <Error name="income_verification" />
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              </ContentWrapper>
              <DefaultButtonBar handleBack={handleBack}>
                <Button
                  theme="primary"
                  size="small"
                  type="submit"
                  disabled={!isValid}
                  isSubmitting={isSubmitting}
                >
                  {t('common.save', 'Save')}
                </Button>
              </DefaultButtonBar>
            </Form>
          )
        }}
      </Formik>
    </Page>
  )
}

export default memo(EditLivScore)
